// Phase 36 Batch P — Long-Term Projection tab.
//
// User quote: "These projections show how this property will perform as
// a rental in the future."
//
// Layout:
//   ┌──────────────────────────────────────────────────────────────┐
//   │  HEADER  (title + price-basis chip + role caption)           │
//   │  KPI strip — Payback · Total Return at Y10 · Equity at Y30   │
//   │  Assumptions — rent g · expense g · appr. · horizon          │
//   │  Line chart — Cumulative CF + Cumulative Total Return        │
//   │  Year-by-year table (payback row tinted)                     │
//   └──────────────────────────────────────────────────────────────┘
//
// Re-fetches on:
//   • assumption input change (rent_growth_pct / expense_growth_pct /
//     appreciation_pct / years)
//   • role / counter / price_basis_value changes ON THE PARENT DEAL
//     (not handled here — DealDetail unmounts the tab; we just key off
//     `dealId` and a stamp passed in via prop so a parent change re-
//     mounts us)
//
// Stale-banner integration: the assumption inputs are LOCAL — they do
// NOT touch the underlying deal analysis, so they don't trigger
// StaleAnalysisBanner. (Confirmed by reading components/StaleAnalysisBanner —
// it's hooked off a separate analyzed_at timestamp on the deal.)

import { useEffect, useMemo, useState } from "react";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { useFmt } from "@/lib/useFmt";
import { TrendingUp, RotateCcw, AlertTriangle, Download, Loader2 } from "lucide-react";

const DEFAULTS = {
  rent_growth_pct: 3.0,
  expense_growth_pct: 2.5,
  appreciation_pct: 3.0,
  years: 30,
};

const HORIZONS = [5, 10, 15, 20, 25, 30];

function formatYears(n) {
  if (n === null || n === undefined) return "Never";
  return `${Number(n).toFixed(1)} yrs`;
}

function basisLabel(basis) {
  switch (basis) {
    case "counter_offer": return "Counter offer";
    case "asking": return "Asking price";
    case "purchase_price": return "Purchase price (owned)";
    case "asking_seller_view": return "Asking (seller view)";
    default: return basis || "—";
  }
}

export default function ProjectionTab({ dealId }) {
  const fmt = useFmt();
  const [params, setParams] = useState(DEFAULTS);
  const [proj, setProj] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debounced fetch — keystrokes on number inputs would otherwise spam
  // the backend.
  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const t = setTimeout(async () => {
      try {
        const qs = new URLSearchParams({
          years: String(params.years),
          rent_growth_pct: String(params.rent_growth_pct),
          expense_growth_pct: String(params.expense_growth_pct),
          appreciation_pct: String(params.appreciation_pct),
        }).toString();
        const r = await api.get(`/deals/${dealId}/projection?${qs}`);
        if (!cancelled) setProj(r.data);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || "Could not load projection.");
          setProj(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [dealId, params.years, params.rent_growth_pct, params.expense_growth_pct, params.appreciation_pct]);

  const chartData = useMemo(() => {
    if (!proj?.rows) return [];
    return proj.rows.map((r) => ({
      year: r.year,
      "Cumulative CF": r.cumulative_cash_flow,
      "Cumulative Total Return": r.cumulative_total_return,
    }));
  }, [proj]);

  const totalReturnY10 = useMemo(() => {
    const r = proj?.rows?.find((row) => row.year === 10) || (proj?.rows || []).slice(-1)[0];
    return r ? r.cumulative_total_return : null;
  }, [proj]);

  const equityY30 = useMemo(() => {
    const r = (proj?.rows || []).slice(-1)[0];
    return r ? r.total_equity : null;
  }, [proj]);

  const reset = () => setParams(DEFAULTS);

  // Phase 36 Batch Q — fetch the PDF as a blob via the same axios
  // instance (cookie auth) and trigger the browser download. We DON'T
  // use `<a href>` — that would skip the auth header path on hosts
  // that don't auto-attach cookies and would also expose auth-only
  // URLs to users who right-click → copy link.
  const [downloading, setDownloading] = useState(false);
  const downloadPdf = async () => {
    if (!dealId || downloading) return;
    setDownloading(true);
    try {
      const qs = new URLSearchParams({
        years: String(params.years),
        rent_growth_pct: String(params.rent_growth_pct),
        expense_growth_pct: String(params.expense_growth_pct),
        appreciation_pct: String(params.appreciation_pct),
      }).toString();
      const r = await api.get(`/deals/${dealId}/projection/pdf?${qs}`, {
        responseType: "blob",
      });
      // Pull filename out of Content-Disposition; fall back to a sane default.
      const cd = r.headers?.["content-disposition"] || r.headers?.["Content-Disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = m ? m[1] : `runidea-projection-${params.years}yr.pdf`;
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Revoke after a beat — Safari needs the URL alive for the download.
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      // Toast intentionally omitted to keep import surface tight; the
      // browser's own download-failed indicator is sufficient and any
      // 4xx from the API will already be logged in DevTools.
    } finally {
      setDownloading(false);
    }
  };

  if (loading && !proj) {
    return (
      <div className="space-y-4 py-6" data-testid="projection-tab-loading">
        <div className="h-3 w-32 animate-pulse rounded bg-slate-100" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl bg-slate-50" />
          ))}
        </div>
        <div className="h-64 animate-pulse rounded-xl bg-slate-50" />
        <div className="h-96 animate-pulse rounded-xl bg-slate-50" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
        data-testid="projection-tab-error"
      >
        <AlertTriangle className="h-4 w-4" />
        {error}
      </div>
    );
  }

  if (!proj || !Array.isArray(proj.rows) || proj.rows.length === 0) return null;

  const paybackYear = proj.milestones?.payback_at_year ?? null;

  return (
    <div className="space-y-6" data-testid="projection-tab">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold text-slate-900">Long-Term Projection</h2>
            <Badge
              variant="outline"
              className="border-indigo-200 bg-indigo-50 text-[11px] uppercase tracking-wider text-indigo-700"
              data-testid="projection-basis-chip"
            >
              {basisLabel(proj.price_basis)} · {fmt.money(proj.price_basis_value)}
            </Badge>
            <Badge
              variant="outline"
              className="border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600"
            >
              {proj.role}
            </Badge>
          </div>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            These projections show how this property will perform as a rental
            in the future. Year 1 ties to the current cash-flow figures; later
            years compound rent, expenses, and appreciation at the rates below.
          </p>
        </div>
        {/* Phase 36 Batch Q — Download PDF. Disabled while a fetch is
            in-flight; spinner replaces the icon to keep the button
            width steady. */}
        <Button
          type="button"
          variant="outline"
          onClick={downloadPdf}
          disabled={downloading}
          data-testid="projection-download-pdf"
          className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
        >
          {downloading ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-1 h-4 w-4" />
          )}
          {downloading ? "Building…" : "Download PDF"}
        </Button>
      </div>

      {/* ── KPI strip ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="projection-kpi-strip">
        <div
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
          data-testid="projection-kpi-payback"
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Payback period
          </p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
            {formatYears(proj.payback_period_years)}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {paybackYear
              ? `Cumulative CF turns positive in year ${paybackYear}.`
              : proj.payback_period_years === null
                ? "Annual cash flow ≤ 0 — does not pay back at this price."
                : `Simple payback at year-1 CF.`}
          </p>
        </div>
        <div
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
          data-testid="projection-kpi-total-return"
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Total return at Year 10
          </p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
            {totalReturnY10 !== null ? fmt.money(totalReturnY10) : "—"}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Cumulative cash flow + equity gain.
          </p>
        </div>
        <div
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
          data-testid="projection-kpi-equity"
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Equity at year {proj.horizon_years}
          </p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
            {equityY30 !== null ? fmt.money(equityY30) : "—"}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Property value − loan balance.
          </p>
        </div>
      </div>

      {/* ── Assumptions panel ──────────────────────────────────── */}
      <div
        className="rounded-2xl border border-slate-200 bg-slate-50 p-5"
        data-testid="projection-assumptions"
      >
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Assumptions
          </p>
          <button
            type="button"
            onClick={reset}
            data-testid="projection-reset-defaults"
            className="inline-flex items-center gap-1 text-xs text-indigo-600 transition-colors hover:text-indigo-800"
          >
            <RotateCcw className="h-3 w-3" /> Reset to defaults
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <Label className="text-[11px] font-medium text-slate-600">
              Rent growth %
            </Label>
            <Input
              type="number"
              min="0" max="20" step="0.1"
              value={params.rent_growth_pct}
              onChange={(e) =>
                setParams((p) => ({ ...p, rent_growth_pct: Number(e.target.value) }))
              }
              data-testid="projection-input-rent-growth"
              className="mt-1 h-9 bg-white"
            />
          </div>
          <div>
            <Label className="text-[11px] font-medium text-slate-600">
              Expense growth %
            </Label>
            <Input
              type="number"
              min="0" max="20" step="0.1"
              value={params.expense_growth_pct}
              onChange={(e) =>
                setParams((p) => ({ ...p, expense_growth_pct: Number(e.target.value) }))
              }
              data-testid="projection-input-expense-growth"
              className="mt-1 h-9 bg-white"
            />
          </div>
          <div>
            <Label className="text-[11px] font-medium text-slate-600">
              Appreciation %
            </Label>
            <Input
              type="number"
              min="0" max="20" step="0.1"
              value={params.appreciation_pct}
              onChange={(e) =>
                setParams((p) => ({ ...p, appreciation_pct: Number(e.target.value) }))
              }
              data-testid="projection-input-appreciation"
              className="mt-1 h-9 bg-white"
            />
          </div>
          <div>
            <Label className="text-[11px] font-medium text-slate-600">
              Hold horizon (years)
            </Label>
            <Select
              value={String(params.years)}
              onValueChange={(v) => setParams((p) => ({ ...p, years: Number(v) }))}
            >
              <SelectTrigger
                className="mt-1 h-9 bg-white"
                data-testid="projection-input-horizon"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HORIZONS.map((y) => (
                  <SelectItem key={y} value={String(y)} data-testid={`projection-horizon-${y}`}>
                    {y} years
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* ── Chart ──────────────────────────────────────────────── */}
      <div
        className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
        data-testid="projection-chart-card"
      >
        <div className="mb-2 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-indigo-600" />
          <h3 className="text-sm font-semibold text-slate-900">
            Cumulative cash flow vs total return
          </h3>
        </div>
        <div className="h-72 w-full" data-testid="projection-chart">
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="year"
                tick={{ fontSize: 11, fill: "#64748b" }}
                label={{ value: "Year", position: "insideBottom", offset: -2, fontSize: 11, fill: "#64748b" }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#64748b" }}
                tickFormatter={(v) =>
                  Math.abs(v) >= 1_000_000
                    ? `$${(v / 1_000_000).toFixed(1)}M`
                    : Math.abs(v) >= 1_000
                      ? `$${(v / 1_000).toFixed(0)}k`
                      : `$${v}`
                }
              />
              <Tooltip
                formatter={(v) => fmt.money(v)}
                contentStyle={{ borderRadius: 12, fontSize: 12, borderColor: "#e2e8f0" }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone" dataKey="Cumulative CF"
                stroke="#0ea5e9" strokeWidth={2} dot={false}
              />
              <Line
                type="monotone" dataKey="Cumulative Total Return"
                stroke="#6366f1" strokeWidth={2} dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Year-by-year table ─────────────────────────────────── */}
      <div
        className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        data-testid="projection-table-card"
      >
        <div className="border-b border-slate-100 p-5">
          <h3 className="text-sm font-semibold text-slate-900">Year-by-year</h3>
          <p className="mt-1 text-xs text-slate-500">
            All figures in {proj.role === "seller" ? "seller view" : "buyer view"}.
            Payback row tinted.
          </p>
        </div>
        <div className="overflow-x-auto" data-testid="projection-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">Yr</TableHead>
                <TableHead className="text-right">Rent</TableHead>
                <TableHead className="text-right">Expenses</TableHead>
                <TableHead className="text-right">NOI</TableHead>
                <TableHead className="text-right">Debt service</TableHead>
                <TableHead className="text-right">Cash flow</TableHead>
                <TableHead className="text-right">Cum. CF</TableHead>
                <TableHead className="text-right">Loan bal.</TableHead>
                <TableHead className="text-right">Equity</TableHead>
                <TableHead className="text-right">Total return</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {proj.rows.map((r) => {
                const isPayback = paybackYear === r.year;
                return (
                  <TableRow
                    key={r.year}
                    data-testid={`projection-row-${r.year}`}
                    className={
                      isPayback
                        ? "bg-emerald-50/60 hover:bg-emerald-50"
                        : ""
                    }
                  >
                    <TableCell className="font-semibold text-slate-700">
                      {r.year}
                      {isPayback ? (
                        <span className="ml-1 text-[10px] uppercase tracking-wider text-emerald-700">
                          payback
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{fmt.money(r.annual_rent)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt.money(r.annual_expenses)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt.money(r.noi)}</TableCell>
                    <TableCell className="text-right tabular-nums text-slate-500">{fmt.money(r.debt_service)}</TableCell>
                    <TableCell
                      className={`text-right tabular-nums font-medium ${
                        r.annual_cash_flow >= 0 ? "text-emerald-700" : "text-rose-700"
                      }`}
                    >
                      {fmt.money(r.annual_cash_flow)}
                    </TableCell>
                    <TableCell
                      className={`text-right tabular-nums ${
                        r.cumulative_cash_flow >= 0 ? "text-emerald-700" : "text-rose-700"
                      }`}
                    >
                      {fmt.money(r.cumulative_cash_flow)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-slate-500">{fmt.money(r.loan_balance)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt.money(r.total_equity)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium text-indigo-700">
                      {fmt.money(r.cumulative_total_return)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
